import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useRef, type FormEventHandler, type MouseEvent, type ReactNode } from 'react';
import { Button } from '../Button';
import type { DialogCloseReason } from './Dialog';
import { DialogCloseButton } from './DialogCloseButton';

export type DialogActionColor = 'primary' | 'secondary' | 'error' | 'success' | 'warning' | 'info';

export type DialogAction = {
  label: ReactNode;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  loading?: boolean;
  disabled?: boolean;
  /** Contained color for the primary action, outlined color for a secondary one (e.g. `error`). */
  color?: DialogActionColor;
  startIcon?: ReactNode;
  /** Primary action only — focus it when the dialog opens. */
  autoFocus?: boolean;
};

export type DialogCancelAction = {
  /** Default "ביטול". */
  label?: ReactNode;
  /** Default: the dialog's `onClose`. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
};

type DialogLayoutProps = {
  onClose?: (event: object, reason: DialogCloseReason) => void;
  title?: ReactNode;
  primaryAction?: DialogAction;
  cancelAction?: DialogCancelAction | true;
  secondaryActions?: DialogAction[];
  onSubmit?: FormEventHandler<HTMLFormElement>;
  contentPadding?: 'default' | 'none' | 'uniform';
  children?: ReactNode;
};

/** Title + X, body and action row — the same structure in the desktop dialog and the mobile sheet. */
export function DialogLayout({
  onClose,
  title,
  primaryAction,
  cancelAction,
  secondaryActions = [],
  onSubmit,
  contentPadding = 'default',
  children
}: DialogLayoutProps) {
  const cancel = cancelAction === true ? {} : cancelAction;
  const primaryRef = useRef<HTMLButtonElement>(null);
  const autoFocusPrimary = Boolean(primaryAction?.autoFocus);

  // Mounts with the open dialog (and before MUI's focus trap runs), so this focuses on every open.
  useEffect(() => {
    if (autoFocusPrimary) primaryRef.current?.focus();
  }, [autoFocusPrimary]);
  const hasActions = Boolean(cancel || primaryAction || secondaryActions.length);

  const body = (
    <>
      {onClose && (
        <span className="SmDialog-close">
          <DialogCloseButton onClick={event => onClose(event, 'closeButton')} />
        </span>
      )}
      {title !== undefined && title !== null && title !== false && (
        <DialogTitle className={onClose ? 'SmDialog-title SmDialog-title-withClose' : 'SmDialog-title'}>
          {title}
        </DialogTitle>
      )}
      <DialogContent
        className={[
          'SmDialog-content',
          contentPadding === 'none' && 'SmDialog-content-flush',
          contentPadding === 'uniform' && 'SmDialog-content-uniform'
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </DialogContent>
      {hasActions && (
        <DialogActions className="SmDialog-actions">
          {cancel && (
            <Button
              variant="outlined"
              disabled={cancel.disabled}
              onClick={event => (cancel.onClick ? cancel.onClick(event) : onClose?.(event, 'closeButton'))}
            >
              {cancel.label ?? 'ביטול'}
            </Button>
          )}
          {secondaryActions.map((action, index) => (
            <Button
              key={index}
              variant="outlined"
              color={action.color}
              loading={action.loading}
              disabled={action.disabled}
              startIcon={action.startIcon}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
          {primaryAction && (
            <Button
              ref={primaryRef}
              color={primaryAction.color}
              loading={primaryAction.loading}
              disabled={primaryAction.disabled}
              startIcon={primaryAction.startIcon}
              type={onSubmit ? 'submit' : 'button'}
              onClick={primaryAction.onClick}
            >
              {primaryAction.label}
            </Button>
          )}
        </DialogActions>
      )}
    </>
  );

  return onSubmit ? <form onSubmit={onSubmit}>{body}</form> : body;
}
